import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { useCurrency } from '../../../contexts/CurrencyContext';

const PageContainer = styled.div`
  padding: 40px;
  min-height: 100vh;
  background: #f8f9fa;

  @media (max-width: 768px) {
    padding: 24px 12px 32px;
  }
`;

const PlanType = styled.span`
  font-size: 0.875rem;
  color: #666;
  font-weight: 600;

  @media (max-width: 1024px) {
    &:before {
      content: "Tipo do Plano: ";
      font-weight: 500;
      color: #666;
    }
  }
`;

const WelcomeHeader = styled.div`
  background: white;
  padding: 20px 40px;
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  h1 {
    font-size: 1.5rem;
    color: #333;
    font-weight: 500;
  }
`;

const FiltersContainer = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 24px;
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  padding: 12px 16px;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.3s ease;

  &:focus {
    border-color: #ba824f;
    outline: none;
  }
`;

const FilterSelect = styled.select`
  padding: 12px 16px;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
  min-width: 150px;

  &:focus {
    border-color: #ba824f;
    outline: none;
  }
`;

const DateInput = styled.input`
  padding: 12px 16px;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  min-width: 150px;

  &:focus {
    border-color: #ba824f;
    outline: none;
  }
`;

const InvoicesTable = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-top: 8px;

  @media (max-width: 768px) {
    border-radius: 16px;
    margin-top: 12px;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.8fr 2fr 1.1fr 1fr 1fr 1fr 1fr;
  padding: 12px 14px;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
  gap: 6px;
  width: 100%;

  span {
    font-size: 0.75rem;
    color: #666;
    font-weight: 500;
    text-transform: uppercase;
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

const InvoiceItem = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.8fr 2fr 1.1fr 1fr 1fr 1fr 1fr;
  padding: 12px 14px;
  border-bottom: 1px solid #eee;
  align-items: center;
  gap: 6px;
  transition: background 0.3s ease;
  width: 100%;

  &:hover {
    background: #f8f9fa;
  }

  @media (max-width: 1024px) {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }

  @media (max-width: 768px) {
    padding: 16px 18px;
    border-bottom: 1px solid #f1f1f1;
    background: #ffffff;
  }
`;

const InvoiceNumber = styled.span`
  font-size: 0.875rem;
  color: #666;

  @media (max-width: 1024px) {
    &:before {
      content: "Número: ";
      font-weight: 500;
    }
  }
`;

const PropertyName = styled.span`
  font-size: 0.875rem;
  color: #333;

  @media (max-width: 1024px) {
    &:before {
      content: "Imóvel: ";
      font-weight: 500;
      color: #666;
    }
  }
`;

const InvoiceDate = styled.span`
  font-size: 0.875rem;
  color: #666;

  @media (max-width: 1024px) {
    &:before {
      content: "Data: ";
      font-weight: 500;
    }
  }
`;

const InvoiceAmount = styled.span`
  font-size: 0.875rem;
  color: #333;
  font-weight: 500;

  @media (max-width: 1024px) {
    &:before {
      content: "Valor: ";
      font-weight: 500;
      color: #666;
    }
  }
`;

const InvoiceStatus = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  
  &.paid {
    background: #e6f4ea;
    color: #1e7e34;
  }
  
  &.pending {
    background: #fff3e0;
    color: #f57c00;
  }
  
  &.cancelled {
    background: #ffebee;
    color: #c62828;
  }

  @media (max-width: 1024px) {
    &:before {
      content: "Status: ";
      font-weight: 500;
      color: #666;
    }
  }
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  color: #666;
  border: 1px solid #eee;

  &:hover {
    background: #f8f9fa;
  }

  @media (max-width: 1024px) {
    width: 100%;
  }
`;

const AddInvoiceForm = styled.form`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.08);
  padding: 32px 24px 24px 24px;
  margin-bottom: 28px;
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  align-items: flex-end;
`;

const AddInput = styled.input`
  min-width: 220px;
  flex: 1;
  padding: 12px 14px;
  border-radius: 8px;
  border: 2px solid #eee;
  font-size: 1rem;
`;

const AddLabel = styled.label`
  display: flex;
  flex-direction: column;
  font-size: 0.93rem;
  color: #222;
  font-weight: 500;
  gap: 6px;
`;

const AddButton = styled.button`
  background: #34574A;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 30px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background .2s;
  &:hover { background: #27443a; }
`;

function NewInvoiceForm({ onSuccess }) {
  const [fields, setFields] = useState({
    usuarioFATURA: '',
    planoFATURA: '',
    numeroFATURA: '',
    dataFATURA: '',
    linkPDF_FATURA: '',
    statusFATURA: '',
    valorFATURA: '',
    checkoutFATURA: '',
    ativoFATURA: 1
  });
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  function handleChange(e) {
    const { name, value } = e.target;
    setFields(f => ({ ...f, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setFeedback(null);
    try {
      const res = await fetch(`${import.meta.env.VITE_URL_API}/faturas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'accept': 'application/json' },
        body: JSON.stringify({
          ...fields,
          valorFATURA: Number(fields.valorFATURA),
          checkoutFATURA: Number(fields.checkoutFATURA),
          ativoFATURA: Number(fields.ativoFATURA)
        })
      });
      if (!res.ok) throw new Error('Erro ao cadastrar fatura');
      setFeedback({ type: 'success', message: 'Fatura cadastrada com sucesso!' });
      setFields({
        usuarioFATURA: '', planoFATURA: '', numeroFATURA: '', dataFATURA: '', linkPDF_FATURA: '', statusFATURA: '', valorFATURA: '', checkoutFATURA: '', ativoFATURA: 1
      });
      if (onSuccess) onSuccess();
    } catch (err) {
      setFeedback({ type: 'error', message: err.message || 'Erro ao cadastrar fatura' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <AddInvoiceForm onSubmit={handleSubmit}>
      <AddLabel>
        E-mail do usuário
        <AddInput name="usuarioFATURA" value={fields.usuarioFATURA} onChange={handleChange} required type="email" />
      </AddLabel>
      <AddLabel>
        Plano
        <AddInput name="planoFATURA" value={fields.planoFATURA} onChange={handleChange} required />
      </AddLabel>
      <AddLabel>
        Número da Fatura
        <AddInput name="numeroFATURA" value={fields.numeroFATURA} onChange={handleChange} required />
      </AddLabel>
      <AddLabel>
        Data (ISO)
        <AddInput name="dataFATURA" value={fields.dataFATURA} onChange={handleChange} required type="datetime-local" />
      </AddLabel>
      <AddLabel>
        Link PDF
        <AddInput name="linkPDF_FATURA" value={fields.linkPDF_FATURA} onChange={handleChange} required type="url" />
      </AddLabel>
      <AddLabel>
        Status
        <AddInput name="statusFATURA" value={fields.statusFATURA} onChange={handleChange} required placeholder="Pendente, Pago, Cancelado..." />
      </AddLabel>
      <AddLabel>
        Valor (R$)
        <AddInput name="valorFATURA" value={fields.valorFATURA} onChange={handleChange} required type="number" step="0.01" />
      </AddLabel>
      <AddLabel>
        Checkout ID
        <AddInput name="checkoutFATURA" value={fields.checkoutFATURA} onChange={handleChange} required type="number" />
      </AddLabel>
      <AddLabel>
        Ativo
        <AddInput name="ativoFATURA" value={fields.ativoFATURA} onChange={handleChange} required type="number" min="0" max="1" />
      </AddLabel>
      <AddButton type="submit" disabled={loading}>{loading ? 'Salvando...' : 'Cadastrar Fatura'}</AddButton>
      {feedback && (
        <div style={{width:'100%',textAlign:'center',color:feedback.type==='success'?'#34574A':'#B00020',marginTop:12,fontWeight:600}}>
          {feedback.message}
        </div>
      )}
    </AddInvoiceForm>
  );
}

// Função para regerar fatura via Moloni
async function handleRegenerateFatura(checkoutId, onSuccess) {
  const result = await Swal.fire({
    title: 'Regenerar Fatura?',
    text: 'Deseja tentar gerar a fatura novamente via Moloni?',
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#34574A',
    cancelButtonColor: '#d33',
    confirmButtonText: 'Sim, regenerar',
    cancelButtonText: 'Cancelar',
    reverseButtons: true
  });
  if (!result.isConfirmed) return;

  try {
    const token = localStorage.getItem('auth_token');
    toast.info('Tentando regerar fatura...', { position: 'top-center' });
    
    const response = await fetch(`${import.meta.env.VITE_URL_API}/moloni/faturas/${checkoutId}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.ok) {
      const data = await response.json();
      console.log('[Invoices] Fatura regerada com sucesso:', data);
      toast.success('Fatura regerada com sucesso!', { position: 'top-center' });
      onSuccess && onSuccess();
    } else {
      const errorText = await response.text();
      console.error('[Invoices] Erro ao regerar fatura:', errorText);
      toast.error(`Erro ao regerar fatura: ${errorText}`, { position: 'top-center' });
    }
  } catch (err) {
    console.error('[Invoices] Exceção ao regerar fatura:', err);
    toast.error('Erro ao regerar fatura!', { position: 'top-center' });
  }
}

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [plansIndex, setPlansIndex] = useState({ byCode: {}, byName: {} });
  const [planTypesIndex, setPlanTypesIndex] = useState({ byCode: {} });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planoFilter, setPlanoFilter] = useState('all');
  const [itemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const generatePdfFromInvoice = (invoice) => {
    if (!invoice) return;
    const planoNome = invoice.checkout?.nomeCHECKOUT || invoice.plano?.nomePLANO || '-';
    const statusPago = invoice.checkout && typeof invoice.checkout.ativoCHECKOUT !== 'undefined'
      ? (invoice.checkout.ativoCHECKOUT === 1 ? 'Pago' : 'Não Pago')
      : 'Não Pago';
    const rawDate = invoice?.dataFATURA || invoice?.dataCadastroFATURA || invoice?.created_at || invoice?.createdAt || invoice?.updated_at || invoice?.checkout?.dataCHECKOUT || invoice?.checkout?.created_at;
    const date = rawDate ? new Date(rawDate) : null;
    const dateStr = (date && !Number.isNaN(date.getTime()))
      ? `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`
      : '-';
    const pick = (v) => (v !== null && v !== undefined && v !== '') ? v : undefined;
    const rawAmount = pick(invoice?.checkout?.precoCHECKOUT) ?? pick(invoice?.plano?.valorPLANO) ?? pick(invoice?.valorFATURA) ?? 0;
    const amount = formatCurrency(parseAmount(rawAmount));
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" />
      <title>Fatura ${invoice.numeroFATURA || ''}</title>
      <style>
        :root {
          color-scheme: light;
        }
        * { box-sizing: border-box; }
        body {
          font-family: "Segoe UI", Arial, sans-serif;
          background: #f7f7f7;
          padding: 32px;
          color: #0f172a;
        }
        .page {
          max-width: 820px;
          margin: 0 auto;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 28px;
          box-shadow: 0 4px 18px rgba(0,0,0,0.06);
        }
        header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
        .title { font-size: 1.8rem; font-weight: 700; color: #1f2a44; margin: 0; }
        .meta { text-align: right; color: #6b7280; font-size: 0.95rem; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; }
        th, td { text-align: left; padding: 10px 8px; border: 1px solid #e5e7eb; }
        th { background: #f3f4f6; color: #374151; font-weight: 700; }
        td.label { width: 30%; font-weight: 600; color: #4b5563; background: #f9fafb; }
        td.value { color: #111827; }
        .pill { display: inline-block; padding: 4px 10px; border-radius: 10px; font-weight: 700; font-size: 0.95rem; color: #fff; background: ${statusPago === 'Pago' ? '#16a34a' : '#dc2626'}; }
        footer { margin-top: 18px; text-align: right; color: #9ca3af; font-size: 0.9rem; }
        @media print {
          body { background: #fff; padding: 0; }
          .page { border: none; border-radius: 0; box-shadow: none; padding: 20mm; max-width: none; }
          header { margin-bottom: 12px; }
        }
      </style>
    </head><body>
      <div class="page">
        <header>
          <div>
            <p class="meta">${dateStr}</p>
            <h1 class="title">Fatura</h1>
          </div>
          <div class="meta">
            <div>Número: <strong>${invoice.numeroFATURA ?? '-'}</strong></div>
            <div>Código: <strong>${invoice.codigoFATURA ?? '-'}</strong></div>
          </div>
        </header>
        <table>
          <tbody>
            <tr><td class="label">Plano</td><td class="value">${planoNome}</td></tr>
            <tr><td class="label">Data</td><td class="value">${dateStr}</td></tr>
            <tr><td class="label">Valor</td><td class="value">${amount}</td></tr>
            <tr><td class="label">Status</td><td class="value"><span class="pill">${statusPago}</span></td></tr>
          </tbody>
        </table>
        <footer>Gerado por JoviPlanet</footer>
      </div>
      <script>
        window.onload = function() {
          setTimeout(() => { window.print(); }, 150);
        }
      </script>
    </body></html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  // Carregar faturas da API
  async function fetchInvoices() {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${import.meta.env.VITE_URL_API}/faturas`, {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      setInvoices(Array.isArray(data) ? data : []);
    } catch {
      setInvoices([]);
    }
    setLoading(false);
  }

  async function fetchPlansIndex() {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${import.meta.env.VITE_URL_API}/planos`, {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      const byCode = {};
      const byName = {};
      for (const p of list) {
        const code = p?.codigoPLANO;
        if (code !== null && code !== undefined && String(code).trim() !== '') {
          byCode[String(code).trim()] = p;
        }
        const name = p?.nomePLANO;
        if (name && String(name).trim() !== '') {
          byName[String(name).trim().toLowerCase()] = p;
        }
      }
      setPlansIndex({ byCode, byName });
    } catch {
      setPlansIndex({ byCode: {}, byName: {} });
    }
  }

  async function fetchPlanTypesIndex() {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${import.meta.env.VITE_URL_API}/tipos-planos`, {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      const byCode = {};
      for (const t of list) {
        const code = t?.codigoTIPOPLANO ?? t?.codigoTIPO_PLANO ?? t?.id;
        const label = t?.planoTIPOPLANO ?? t?.nomeTIPOPLANO ?? t?.nome ?? t?.label;
        if (code !== null && code !== undefined && String(code).trim() !== '' && label) {
          byCode[String(code).trim()] = String(label).trim();
        }
      }
      setPlanTypesIndex({ byCode });
    } catch {
      setPlanTypesIndex({ byCode: {} });
    }
  }

  useEffect(() => {
    fetchInvoices();
    fetchPlansIndex();
    fetchPlanTypesIndex();
  }, []);

  // Filtros combinados: status, plano e busca
  const { currencySymbol, currency, exchangeRate } = useCurrency();

  const filtered = invoices.filter(invoice => {
    // Busca por código ou número
    const search = searchTerm.trim().toLowerCase();
    const matchesSearch =
      !search ||
      (invoice.codigoFATURA && String(invoice.codigoFATURA).toLowerCase().includes(search)) ||
      (invoice.numeroFATURA && String(invoice.numeroFATURA).toLowerCase().includes(search));

    // Filtro por status (ativoCHECKOUT: 1=Pago, 0=Não Pago)
    let matchesStatus = true;
    if (statusFilter !== 'all') {
      const ativo = invoice.checkout && typeof invoice.checkout.ativoCHECKOUT !== 'undefined' ? String(invoice.checkout.ativoCHECKOUT) : '0';
      matchesStatus = ativo === statusFilter;
    }
    // Filtro por plano
    let matchesPlano = true;
    if (planoFilter && planoFilter !== 'all') {
      matchesPlano = invoice.checkout && invoice.checkout.nomeCHECKOUT === planoFilter;
    }
    return matchesSearch && matchesStatus && matchesPlano;
  });

  // Sempre que mudar o filtro ou o número de itens por página, volta para página 1
  useEffect(() => {
    setCurrentPage(1);
  }, [filtered.length, itemsPerPage]);
  const paginatedInvoices = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    Math.min(currentPage * itemsPerPage, filtered.length)
  );

  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  // Helpers locais para parse/format de moeda
  const parseAmount = (value) => {
    if (value === null || value === undefined) return NaN;
    if (typeof value === 'number') return value;
    let s = String(value).trim();
    if (!s) return NaN;
    // remove símbolos e espaços
    s = s.replace(/[^0-9.,-]/g, '');
    const hasComma = s.includes(',');
    const hasDot = s.includes('.');
    if (hasComma && hasDot) {
      // vírgula como decimal (pt): último separador é vírgula
      if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
        s = s.replace(/\./g, '').replace(/,/g, '.');
      } else {
        s = s.replace(/,/g, '');
      }
    } else if (hasComma) {
      s = s.replace(/\./g, '').replace(/,/g, '.');
    } else {
      s = s.replace(/,/g, '');
    }
    const n = Number(s);
    return Number.isFinite(n) ? n : NaN;
  };

  const formatCurrency = (n) => {
    if (!Number.isFinite(n)) return '-';
    const rate = Number(exchangeRate) || 1;
    const converted = Math.round((n * rate + Number.EPSILON) * 100) / 100;
    return `${currencySymbol || currency || '€'} ${converted.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const resolvePlanForInvoice = (invoice) => {
    const direct = invoice?.plano;
    if (direct && typeof direct === 'object') return direct;

    const code = invoice?.planoFATURA ?? invoice?.planoFATURA?.codigoPLANO ?? invoice?.planoFATURA?.id;
    if (code !== null && code !== undefined && String(code).trim() !== '') {
      const hit = plansIndex.byCode[String(code).trim()];
      if (hit) return hit;
    }

    const name = invoice?.checkout?.nomeCHECKOUT ?? invoice?.planoFATURA ?? invoice?.planoFATURA?.nomePLANO;
    if (name && String(name).trim() !== '') {
      const hit = plansIndex.byName[String(name).trim().toLowerCase()];
      if (hit) return hit;
    }

    return null;
  };

  const resolvePlanTypeLabel = (invoice) => {
    const plano = resolvePlanForInvoice(invoice);
    const raw =
      plano?.tipoPlano?.planoTIPOPLANO
      ?? plano?.tipoPlano?.nomeTIPOPLANO
      ?? plano?.tipoPlano?.nome
      ?? plano?.tipoPLANO
      ?? plano?.tipoPlano?.codigoTIPOPLANO
      ?? invoice?.tipoPlano?.planoTIPOPLANO
      ?? invoice?.checkout?.tipoCHECKOUT
      ?? invoice?.checkout?.tipo
      ?? '';

    const rawStr = String(raw ?? '').trim();
    if (!rawStr) return '-';

    // Se vier como código numérico do tipo, resolve pelo índice de tipos
    if (/^\d+$/.test(rawStr)) {
      const label = planTypesIndex.byCode[rawStr];
      if (label) return label;
    }

    const s = rawStr;
    const norm = rawStr
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

    if (norm.includes('part')) return 'Particular';
    if (norm.includes('arrend')) return 'Arrendar';
    if (norm.includes('prof')) return 'Profissional';
    if (norm.includes('fer')) return 'Férias';
    if (norm.includes('dest')) return 'Destaque';

    return s;
  };

  return (
    <PageContainer>
      <WelcomeHeader>
        <h1>Faturas</h1>
      </WelcomeHeader>

      {/* Filtros */}
      <FiltersContainer>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <label style={{ fontWeight: 500, fontSize: 14, color: '#34574A' }}>Status</label>
          <FilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">Todos</option>
            <option value="1">Pago</option>
            <option value="0">Não Pago</option>
          </FilterSelect>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <label style={{ fontWeight: 500, fontSize: 14, color: '#34574A' }}>Plano</label>
          <FilterSelect value={planoFilter} onChange={e => setPlanoFilter(e.target.value)}>
            <option value="all">Todos</option>
            {Array.from(new Set(invoices.map(inv => inv.checkout && inv.checkout.nomeCHECKOUT).filter(Boolean))).map(nome => (
              <option key={nome} value={nome}>{nome}</option>
            ))}
          </FilterSelect>
        </div>

        <div style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontWeight: 500, fontSize: 14, color: '#34574A' }}>Buscar</label>
          <SearchInput
            type="text"
            placeholder="Buscar código ou número"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </FiltersContainer>
      <InvoicesTable>
        <TableHeader>
          <span>CÓDIGO</span>
          <span>NÚMERO</span>
          <span>PLANO</span>
          <span>TIPO</span>
          <span>DATA</span>
          <span>VALOR</span>
          <span>STATUS</span>
          <span>PDF</span>
        </TableHeader>
        {loading ? <div style={{padding:40, textAlign:'center'}}>Carregando...</div> : paginatedInvoices.map(invoice => {
          const statusPago = invoice.checkout && typeof invoice.checkout.ativoCHECKOUT !== 'undefined'
            ? (invoice.checkout.ativoCHECKOUT === 1 ? 'Pago' : 'Não Pago')
            : 'Não Pago';
          return (
            <InvoiceItem key={invoice.codigoFATURA}>
              <span>{invoice.codigoFATURA}</span>
              <InvoiceNumber>{invoice.numeroFATURA}</InvoiceNumber>
              <PropertyName>{
                invoice.plano && invoice.plano.tipoPlano && invoice.plano.tipoPlano.planoTIPOPLANO
                  ? `plano${invoice.plano.tipoPlano.planoTIPOPLANO} - ${invoice.plano.nomePLANO}`
                  : (invoice.plano && invoice.plano.nomePLANO ? invoice.plano.nomePLANO : '-')
              }</PropertyName>
              <PlanType>{resolvePlanTypeLabel(invoice)}</PlanType>
              <InvoiceDate>{(() => {
                const rawDate = invoice?.dataFATURA
                  || invoice?.dataCadastroFATURA
                  || invoice?.created_at
                  || invoice?.createdAt
                  || invoice?.updated_at
                  || invoice?.checkout?.dataCHECKOUT
                  || invoice?.checkout?.created_at;
                if (!rawDate) return '-';
                const date = new Date(rawDate);
                if (Number.isNaN(date.getTime())) return '-';
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${day}/${month}/${year}`;
              })()}</InvoiceDate>
              <InvoiceAmount>{(() => {
                const pick = (v) => (v !== null && v !== undefined && v !== '') ? v : undefined;
                const raw = pick(invoice?.checkout?.precoCHECKOUT) ?? pick(invoice?.plano?.valorPLANO) ?? pick(invoice?.valorFATURA) ?? 0;
                const n = parseAmount(raw);
                return formatCurrency(n);
              })()}</InvoiceAmount>
              <span>
                <span style={{
                  display:'inline-block',
                  padding:'4px 14px',
                  borderRadius:12,
                  color:'#fff',
                  fontWeight:600,
                  background: statusPago === 'Pago' ? '#3bbd5a' : '#d62a2a',
                  fontSize:'0.95em'
                }}>{statusPago}</span>
              </span>
              <span>
                {(() => { const u = invoice?.linkPDF_FATURA || invoice?.linkPDF || invoice?.pdfUrl || invoice?.urlPDF; return u && String(u).trim() !== ''; })() ? (
                  <ActionButton
                    style={{padding:'6px 12px',fontSize:'0.95em'}}
                    onClick={() => {
                      const url = invoice?.linkPDF_FATURA || invoice?.linkPDF || invoice?.pdfUrl || invoice?.urlPDF;
                      console.log('[Invoices] Abrindo PDF:', url);
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    Visualizar PDF
                  </ActionButton>
                ) : (
                  <>
                    <ActionButton
                      style={{padding:'6px 12px',fontSize:'0.95em',background:'#34574A',color:'#fff',border:'none',marginRight:'4px'}}
                      onClick={() => generatePdfFromInvoice(invoice)}
                      title="Gerar PDF via impressão"
                    >
                      Gerar PDF
                    </ActionButton>
                    {invoice?.checkout?.codigoCHECKOUT && (
                      <ActionButton
                        style={{padding:'6px 12px',fontSize:'0.95em',background:'#f59e0b',color:'#fff',border:'none'}}
                        onClick={() => handleRegenerateFatura(invoice.checkout.codigoCHECKOUT, fetchInvoices)}
                        title="Tentar gerar fatura via Moloni novamente"
                      >
                        Regenerar
                      </ActionButton>
                    )}
                  </>
                )}
              </span>
            </InvoiceItem>
          );
        })}
      </InvoicesTable>
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', margin: '24px 0', gap: 8 }}>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #eee', background: '#fff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
          >Anterior</button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: '1px solid #eee',
                background: currentPage === i + 1 ? '#34574A' : '#fff',
                color: currentPage === i + 1 ? '#fff' : '#34574A',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >{i + 1}</button>
          ))}
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #eee', background: '#fff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
          >Próxima</button>
        </div>
      )}
    </PageContainer>
  );
}

export default Invoices;